import React, { useState, useMemo } from "react";
import { useNavigationEvent } from "navigation-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTruck,
  faSearch,
  faSpinner,
  faTimes,
  faRefresh,
  faClose,
  faCheckCircle,
  faCalendarAlt,
  faDollarSign,
  faBoxes,
  faChartLine,
  faPrint,
  faEye,
  faFileInvoice,
  faFilter,
  faEdit,
  faTrash,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import useSWR from "swr";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import MessageModal from "./MessageModal";

const fetcher = async (url) => {
  const response = await fetch(url, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok)
    throw new Error(`API request failed with status ${response.status}`);
  return response.json();
};

const formatCurrency = (amount) => {
  return `GH₵ ${parseFloat(amount || 0).toFixed(2)}`;
};

const formatDisplayDate = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const formatDisplayDateTime = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function ReceivedStockRegister() {
  const { stateNavigator } = useNavigationEvent();
  const [searchTerm, setSearchTerm] = useState("");
  const [dateFilter, setDateFilter] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedMovement, setSelectedMovement] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modalSuccessMsg, setModalSuccessMsg] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [modalError, setModalError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loadingReceipt, setLoadingReceipt] = useState(false);
  const [editFormData, setEditFormData] = useState({
    quantity: 0,
    unit_cost: 0,
    purchase_order: "",
    notes: "",
  });

  // Fetch inventory items
  const { data: inventoryData, mutate: mutateInventory } = useSWR(
    `${process.env.REACT_APP_BASE_URL}/api/inventory/items`,
    fetcher,
  );

  // Fetch stock movements (receives)
  const {
    data: movementsData,
    error,
    isLoading,
    mutate,
  } = useSWR(
    `${process.env.REACT_APP_BASE_URL}/api/stock/movements?type=receive`,
    fetcher,
    { revalidateOnFocus: true, refreshInterval: 30000 },
  );

  // Create item lookup
  const itemLookup = useMemo(() => {
    if (!inventoryData?.data) return {};
    const lookup = {};
    inventoryData.data.forEach((item) => {
      lookup[item.id] = item.item_name;
    });
    return lookup;
  }, [inventoryData]);

  // Process received stock movements
  const receivedMovements = useMemo(() => {
    if (!movementsData?.data) return [];

    let movements = movementsData.data.filter(
      (m) => m.movement_type === "receive",
    );

    // Apply date filters
    if (dateFilter === "today") {
      const today = new Date().toISOString().split("T")[0];
      movements = movements.filter(
        (m) => m.created_at?.split("T")[0] === today,
      );
    } else if (dateFilter === "week") {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      movements = movements.filter((m) => new Date(m.created_at) >= weekAgo);
    } else if (dateFilter === "month") {
      const monthAgo = new Date();
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      movements = movements.filter((m) => new Date(m.created_at) >= monthAgo);
    } else if (dateFilter === "custom" && startDate && endDate) {
      movements = movements.filter((m) => {
        const date = new Date(m.created_at).toISOString().split("T")[0];
        return date >= startDate && date <= endDate;
      });
    }

    if (searchTerm) {
      movements = movements.filter(
        (m) =>
          (itemLookup[m.inventory_item_id] || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (m.reference_number || "")
            .toLowerCase()
            .includes(searchTerm.toLowerCase()),
      );
    }

    return movements.sort(
      (a, b) => new Date(b.created_at) - new Date(a.created_at),
    );
  }, [movementsData, dateFilter, startDate, endDate, searchTerm, itemLookup]);

  // Calculate statistics
  const statistics = useMemo(() => {
    const totalReceipts = receivedMovements.length;
    const totalUnitsReceived = receivedMovements.reduce(
      (sum, m) => sum + Math.abs(m.quantity),
      0,
    );
    const totalValueReceived = receivedMovements.reduce(
      (sum, m) => sum + Math.abs(m.quantity) * (m.unit_cost || 0),
      0,
    );
    const uniqueSuppliers = new Set(
      receivedMovements
        .map((m) => m.reference_number?.split("-")[0])
        .filter(Boolean),
    ).size;
    const totalPurchaseOrders = new Set(
      receivedMovements.map((m) => m.reference_number).filter(Boolean),
    ).size;

    // Get top received items
    const itemCounts = {};
    receivedMovements.forEach((m) => {
      const name = itemLookup[m.inventory_item_id] || "Unknown";
      itemCounts[name] = (itemCounts[name] || 0) + Math.abs(m.quantity);
    });
    const topItems = Object.entries(itemCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);

    return {
      totalReceipts,
      totalUnitsReceived,
      totalValueReceived: totalValueReceived.toFixed(2),
      uniqueSuppliers,
      totalPurchaseOrders,
      topItems,
    };
  }, [receivedMovements, itemLookup]);

  // Pagination
  const totalPages = Math.ceil(receivedMovements.length / itemsPerPage);
  const paginatedMovements = receivedMovements.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  // Fetch CSRF token
  const fetchCsrfToken = async () => {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/session/token`,
      {
        method: "GET",
        credentials: "include",
      },
    );
    if (!response.ok) throw new Error("Failed to fetch CSRF token");
    return await response.text();
  };

  const handleEditReceived = async (e) => {
    e.preventDefault();

    if (!selectedMovement) return;
    if (editFormData.quantity <= 0) {
      setErrorMessage("Please enter a valid quantity");
      setModalError(true);
      return;
    }
    if (editFormData.unit_cost < 0) {
      setErrorMessage("Unit cost cannot be negative");
      setModalError(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const csrfToken = await fetchCsrfToken();

      // Use PATCH method
      const updateResponse = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/stock/movement/${selectedMovement.id}`,
        {
          method: "PATCH", // Using PATCH
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            quantity: editFormData.quantity,
            unit_cost: editFormData.unit_cost,
            reference_number: editFormData.purchase_order,
            reason: editFormData.notes,
          }),
        },
      );

      if (updateResponse.ok) {
        await mutate();
        await mutateInventory();
        setShowEditModal(false);
        setSelectedMovement(null);
        setSuccessMessage(`Receipt record updated successfully!`);
        setModalSuccessMsg(true);
      } else {
        const errorData = await updateResponse.json();
        setErrorMessage(errorData.error || "Failed to update receipt");
        setModalError(true);
      }
    } catch (err) {
      console.error("Error updating receipt:", err);
      setErrorMessage("An error occurred while updating the receipt");
      setModalError(true);
    }
    setIsSubmitting(false);
  };

  // Delete received stock (reverse the stock movement)
  const handleDeleteReceived = async () => {
    if (!selectedMovement) return;

    setIsSubmitting(true);

    try {
      const csrfToken = await fetchCsrfToken();

      // Reverse the stock movement (subtract the quantity from inventory)
      const reverseResponse = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/inventory/reverse-receipt`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": csrfToken,
          },
          body: JSON.stringify({
            item_id: selectedMovement.inventory_item_id,
            quantity: Math.abs(selectedMovement.quantity),
            receipt_id: selectedMovement.id,
            reason: `Reversing receipt #${selectedMovement.id}: ${selectedMovement.reason || "Incorrect receipt"}`,
          }),
        },
      );

      if (!reverseResponse.ok) {
        const errorData = await reverseResponse.json();
        throw new Error(errorData.error || "Failed to reverse stock");
      }

      // Delete the movement record
      const deleteResponse = await fetch(
        `${process.env.REACT_APP_BASE_URL}/api/stock/movement/${selectedMovement.id}`,
        {
          method: "DELETE",
          credentials: "include",
          headers: {
            "X-CSRF-Token": csrfToken,
          },
        },
      );

      if (deleteResponse.ok) {
        await mutate(); // Refresh movements list
        await mutateInventory(); // Refresh inventory
        setShowDeleteConfirm(false);
        setSelectedMovement(null);
        setSuccessMessage(`Receipt record deleted and stock reversed!`);
        setModalSuccessMsg(true);
      } else {
        const errorData = await deleteResponse.json();
        setErrorMessage(errorData.error || "Failed to delete receipt");
        setModalError(true);
      }
    } catch (err) {
      console.error("Error deleting receipt:", err);
      setErrorMessage("An error occurred while deleting the receipt");
      setModalError(true);
    }
    setIsSubmitting(false);
  };

  const handleViewReceipt = (movement) => {
    setSelectedMovement(movement);
    setLoadingReceipt(false);
    setShowReceiptModal(true);
  };

  const handleEditClick = (movement) => {
    setSelectedMovement(movement);
    setEditFormData({
      quantity: Math.abs(movement.quantity),
      unit_cost: movement.unit_cost || 0,
      purchase_order: movement.reference_number || "",
      notes: movement.reason || "",
    });
    setShowEditModal(true);
  };

  const handleDeleteClick = (movement) => {
    setSelectedMovement(movement);
    setShowDeleteConfirm(true);
  };

  const handlePrintReceipt = () => {
    const printContent = document.getElementById("receipt-content");
    if (printContent) {
      const originalContents = document.body.innerHTML;
      document.body.innerHTML = printContent.innerHTML;
      window.print();
      document.body.innerHTML = originalContents;
      window.location.reload();
    }
  };

  const handleRefresh = () => {
    mutate();
    setCurrentPage(1);
  };

  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  if (isLoading && !movementsData) {
    return (
      <div className="dashboard-container">
        <DashboardContentHeader />
        <div className="content-wrapper">
          <DashboardContenLeftMenus />
          <div style={styles.container}>
            <div style={styles.loadingContainer}>
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                style={{ fontSize: "48px", color: "#FF6B35" }}
              />
              <p>Loading received stock records...</p>
            </div>
          </div>
        </div>
        <DashboardContentFooter />
      </div>
    );
  }

  if (error && !movementsData) {
    return (
      <div className="dashboard-container">
        <DashboardContentHeader />
        <div className="content-wrapper">
          <DashboardContenLeftMenus />
          <div style={styles.container}>
            <div style={styles.errorContainer}>
              <FontAwesomeIcon
                icon={faTimes}
                style={{ fontSize: "48px", color: "#d32f2f" }}
              />
              <h3>Error Loading Data</h3>
              <p>Unable to connect to the API. Please check your connection.</p>
              <button style={styles.retryButton} onClick={handleRefresh}>
                <FontAwesomeIcon
                  icon={faRefresh}
                  style={{ marginRight: "8px" }}
                />
                Retry
              </button>
            </div>
          </div>
        </div>
        <DashboardContentFooter />
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div style={styles.container}>
          <TopActionMenus AddTitle="Received Stock Register" />

          {/* STATISTICS CARDS - ROW 1 */}
          <div style={styles.statisticsGrid}>
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#E8F5E9",
                }}
              >
                <FontAwesomeIcon
                  icon={faTruck}
                  style={{ color: "#4CAF50", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.totalReceipts}</h3>
                <p style={styles.statLabel}>Total Receipts</p>
              </div>
            </div>

            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#E3F2FD",
                }}
              >
                <FontAwesomeIcon
                  icon={faBoxes}
                  style={{ color: "#2196F3", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>
                  {statistics.totalUnitsReceived.toLocaleString()}
                </h3>
                <p style={styles.statLabel}>Total Units Received</p>
              </div>
            </div>

            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#FCE4EC",
                }}
              >
                <FontAwesomeIcon
                  icon={faDollarSign}
                  style={{ color: "#E91E63", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>
                  {formatCurrency(statistics.totalValueReceived)}
                </h3>
                <p style={styles.statLabel}>Total Value Received</p>
              </div>
            </div>
          </div>

          {/* STATISTICS CARDS - ROW 2 */}
          <div style={styles.statisticsGrid}>
            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#FFF3E0",
                }}
              >
                <FontAwesomeIcon
                  icon={faChartLine}
                  style={{ color: "#FF9800", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>
                  {statistics.totalPurchaseOrders}
                </h3>
                <p style={styles.statLabel}>Purchase Orders</p>
              </div>
            </div>

            <div style={styles.statCard}>
              <div
                style={{
                  ...styles.statIconContainer,
                  backgroundColor: "#F3E5F5",
                }}
              >
                <FontAwesomeIcon
                  icon={faChartLine}
                  style={{ color: "#9C27B0", fontSize: "24px" }}
                />
              </div>
              <div>
                <h3 style={styles.statValue}>{statistics.uniqueSuppliers}</h3>
                <p style={styles.statLabel}>Unique Suppliers</p>
              </div>
            </div>
          </div>

          {/* TOP ITEMS CARD */}
          <div style={styles.topItemsCard}>
            <h3 style={styles.sectionTitle}>Top Received Items</h3>
            <div style={styles.topItemsList}>
              {statistics.topItems.map(([name, qty], idx) => (
                <div key={idx} style={styles.topItem}>
                  <span style={styles.topItemRank}>{idx + 1}</span>
                  <span style={styles.topItemName}>{name}</span>
                  <span style={styles.topItemQty}>
                    {qty.toLocaleString()} units
                  </span>
                </div>
              ))}
              {statistics.topItems.length === 0 && (
                <div style={styles.noDataText}>No data available</div>
              )}
            </div>
          </div>

          {/* FILTERS BAR */}
          <div style={styles.filtersBar}>
            <div style={styles.searchWrapper}>
              <FontAwesomeIcon icon={faSearch} style={styles.searchIcon} />
              <input
                type="text"
                style={styles.searchInput}
                placeholder="Search by item name or PO number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm("")}
                  style={styles.clearSearchButton}
                >
                  ✕
                </button>
              )}
            </div>

            <div style={styles.dateWrapper}>
              <FontAwesomeIcon
                icon={faCalendarAlt}
                style={{ color: "#FF6B35" }}
              />
              <select
                style={styles.dateSelect}
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
                <option value="custom">Custom Range</option>
              </select>
            </div>

            {dateFilter === "custom" && (
              <div style={styles.customDateRange}>
                <input
                  type="date"
                  style={styles.dateInput}
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
                <span>to</span>
                <input
                  type="date"
                  style={styles.dateInput}
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            )}

            <button style={styles.refreshButton} onClick={handleRefresh}>
              <FontAwesomeIcon icon={faRefresh} />
            </button>
          </div>

          {/* RECEIVED STOCK TABLE */}
          {receivedMovements.length === 0 ? (
            <div style={styles.emptyState}>
              <FontAwesomeIcon
                icon={faTruck}
                style={{ fontSize: "64px", color: "#CBD5E1" }}
              />
              <h3 style={styles.emptyTitle}>No Received Stock Records</h3>
              <p style={styles.emptySubtitle}>
                {searchTerm
                  ? "No records match your search"
                  : "No stock receipts found for the selected period"}
              </p>
            </div>
          ) : (
            <>
              <div style={styles.tableCard}>
                <div style={styles.tableContainer}>
                  <table style={styles.table}>
                    <thead>
                      <tr>
                        <th style={styles.th}>Date</th>
                        <th style={styles.th}>Item Name</th>
                        <th style={styles.th}>Quantity</th>
                        <th style={styles.th}>Unit Cost</th>
                        <th style={styles.th}>Total Cost</th>
                        <th style={styles.th}>PO Number</th>
                        <th style={styles.th}>Received By</th>
                        <th style={styles.th}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedMovements.map((movement) => (
                        <tr key={movement.id} style={styles.tableRow}>
                          <td style={styles.td}>
                            <FontAwesomeIcon
                              icon={faCalendarAlt}
                              style={{ marginRight: "6px", color: "#6B7280" }}
                            />
                            {formatDisplayDate(movement.created_at)}
                          </td>
                          <td style={styles.td}>
                            <strong>
                              {itemLookup[movement.inventory_item_id] ||
                                "Unknown"}
                            </strong>
                          </td>
                          <td style={styles.td}>
                            <span style={styles.quantityBadge}>
                              +{Math.abs(movement.quantity)} units
                            </span>
                          </td>
                          <td style={styles.td}>
                            {formatCurrency(movement.unit_cost || 0)}
                          </td>
                          <td style={styles.td}>
                            {formatCurrency(
                              Math.abs(movement.quantity) *
                                (movement.unit_cost || 0),
                            )}
                          </td>
                          <td style={styles.td}>
                            {movement.reference_number || "-"}
                          </td>
                          <td style={styles.td}>
                            {movement.created_by_name || "System"}
                          </td>
                          <td style={styles.td}>
                            <div style={styles.actionButtons}>
                              <button
                                style={styles.viewButton}
                                onClick={() => handleViewReceipt(movement)}
                                title="View Details"
                              >
                                <FontAwesomeIcon icon={faEye} />
                              </button>
                              <button
                                style={styles.editButton}
                                onClick={() => handleEditClick(movement)}
                                title="Edit Receipt"
                              >
                                <FontAwesomeIcon icon={faEdit} />
                              </button>
                              <button
                                style={styles.deleteButton}
                                onClick={() => handleDeleteClick(movement)}
                                title="Delete Receipt"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div style={styles.paginationContainer}>
                  <button
                    style={{
                      ...styles.paginationButton,
                      opacity: canGoPrevious ? 1 : 0.5,
                    }}
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={!canGoPrevious}
                  >
                    Previous
                  </button>
                  <span style={styles.paginationInfo}>
                    Page {currentPage} of {totalPages} (
                    {receivedMovements.length} records)
                  </span>
                  <button
                    style={{
                      ...styles.paginationButton,
                      opacity: canGoNext ? 1 : 0.5,
                    }}
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={!canGoNext}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <DashboardContentFooter />

      {/* RECEIPT MODAL */}
      {showReceiptModal && selectedMovement && (
        <div className="modal">
          <div
            className="overlay"
            onClick={() => setShowReceiptModal(false)}
          ></div>
          <div className="modal-content" style={{ maxWidth: "500px" }}>
            <div className="modal-body">
              <div className="modal-header">
                <p>
                  <FontAwesomeIcon
                    icon={faFileInvoice}
                    style={{ marginRight: "10px", color: "#FF6B35" }}
                  />
                  Stock Receipt Details
                </p>
                <button
                  className="close-modal"
                  onClick={() => setShowReceiptModal(false)}
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>

              {loadingReceipt ? (
                <div style={{ textAlign: "center", padding: "40px" }}>
                  <FontAwesomeIcon
                    icon={faSpinner}
                    spin
                    size="2x"
                    color="#FF6B35"
                  />
                  <p>Loading receipt...</p>
                </div>
              ) : (
                <div id="receipt-content" style={styles.receiptContainer}>
                  <div style={styles.receiptHeader}>
                    <h3 style={styles.receiptTitle}>THE HEADQUARTERS EATERY</h3>
                    <p style={styles.receiptSubtitle}>Stock Receipt</p>
                    <div style={styles.receiptDivider} />
                  </div>

                  <div style={styles.receiptInfo}>
                    <div style={styles.receiptRow}>
                      <span style={styles.receiptLabel}>Receipt ID:</span>
                      <span style={styles.receiptValue}>
                        #{selectedMovement.id}
                      </span>
                    </div>
                    <div style={styles.receiptRow}>
                      <span style={styles.receiptLabel}>Date:</span>
                      <span style={styles.receiptValue}>
                        {formatDisplayDateTime(selectedMovement.created_at)}
                      </span>
                    </div>
                    <div style={styles.receiptRow}>
                      <span style={styles.receiptLabel}>Item:</span>
                      <span style={styles.receiptValue}>
                        <strong>
                          {itemLookup[selectedMovement.inventory_item_id] ||
                            "Unknown"}
                        </strong>
                      </span>
                    </div>
                    <div style={styles.receiptRow}>
                      <span style={styles.receiptLabel}>Quantity:</span>
                      <span
                        style={{ ...styles.receiptValue, color: "#10B981" }}
                      >
                        +{Math.abs(selectedMovement.quantity)} units
                      </span>
                    </div>
                    <div style={styles.receiptRow}>
                      <span style={styles.receiptLabel}>Unit Cost:</span>
                      <span style={styles.receiptValue}>
                        {formatCurrency(selectedMovement.unit_cost || 0)}
                      </span>
                    </div>
                    <div style={styles.receiptRow}>
                      <span style={styles.receiptLabel}>Total Cost:</span>
                      <span style={styles.receiptValue}>
                        {formatCurrency(
                          Math.abs(selectedMovement.quantity) *
                            (selectedMovement.unit_cost || 0),
                        )}
                      </span>
                    </div>
                    {selectedMovement.reference_number && (
                      <div style={styles.receiptRow}>
                        <span style={styles.receiptLabel}>PO Number:</span>
                        <span style={styles.receiptValue}>
                          {selectedMovement.reference_number}
                        </span>
                      </div>
                    )}
                    <div style={styles.receiptRow}>
                      <span style={styles.receiptLabel}>Received By:</span>
                      <span style={styles.receiptValue}>
                        {selectedMovement.created_by_name || "System"}
                      </span>
                    </div>
                    {selectedMovement.reason && (
                      <div style={styles.receiptRow}>
                        <span style={styles.receiptLabel}>Notes:</span>
                        <span style={styles.receiptValue}>
                          {selectedMovement.reason}
                        </span>
                      </div>
                    )}
                  </div>

                  <div style={styles.receiptDivider} />
                  <div style={styles.receiptFooter}>
                    <p>Stock received successfully!</p>
                  </div>
                </div>
              )}

              <div style={styles.modalActions}>
                <button style={styles.printButton} onClick={handlePrintReceipt}>
                  <FontAwesomeIcon
                    icon={faPrint}
                    style={{ marginRight: "8px" }}
                  />
                  Print Receipt
                </button>
                <button
                  style={styles.closeButton}
                  onClick={() => setShowReceiptModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EDIT RECEIPT MODAL */}
      {showEditModal && selectedMovement && (
        <div className="modal">
          <div
            className="overlay"
            onClick={() => setShowEditModal(false)}
          ></div>
          <div className="modal-content" style={{ maxWidth: "500px" }}>
            <div className="modal-body">
              <div className="modal-header">
                <p>
                  <FontAwesomeIcon
                    icon={faEdit}
                    style={{ marginRight: "10px", color: "#FF6B35" }}
                  />
                  Edit Receipt -{" "}
                  {itemLookup[selectedMovement.inventory_item_id]}
                </p>
                <button
                  className="close-modal"
                  onClick={() => setShowEditModal(false)}
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>

              <form onSubmit={handleEditReceived}>
                <div className="form-group">
                  <label>Original Quantity</label>
                  <input
                    type="text"
                    value={`${Math.abs(selectedMovement.quantity)} units`}
                    disabled
                    style={styles.disabledInput}
                  />
                </div>

                <div className="form-group">
                  <label>New Quantity *</label>
                  <input
                    type="number"
                    placeholder="Enter new quantity"
                    value={editFormData.quantity}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        quantity: parseInt(e.target.value) || 0,
                      })
                    }
                    required
                    min="1"
                    autoFocus
                  />
                </div>

                <div className="form-group">
                  <label>Unit Cost (GH₵) *</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={editFormData.unit_cost}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        unit_cost: parseFloat(e.target.value) || 0,
                      })
                    }
                    required
                    min="0"
                  />
                </div>

                <div className="form-group">
                  <label>Purchase Order #</label>
                  <input
                    type="text"
                    placeholder="PO-12345"
                    value={editFormData.purchase_order}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        purchase_order: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    placeholder="Additional notes"
                    rows="2"
                    value={editFormData.notes}
                    onChange={(e) =>
                      setEditFormData({
                        ...editFormData,
                        notes: e.target.value,
                      })
                    }
                  />
                </div>

                <div style={styles.formActions}>
                  <button
                    type="button"
                    style={styles.cancelButton}
                    onClick={() => setShowEditModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    style={styles.submitButton}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <FontAwesomeIcon icon={faSpinner} spin />
                    ) : (
                      "Save Changes"
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* DELETE CONFIRMATION MODAL */}
      {showDeleteConfirm && selectedMovement && (
        <div className="modal">
          <div
            className="overlay"
            onClick={() => setShowDeleteConfirm(false)}
          ></div>
          <div className="modal-content" style={{ maxWidth: "450px" }}>
            <div className="modal-body">
              <div className="modal-header">
                <p>
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    style={{ marginRight: "10px", color: "#EF4444" }}
                  />
                  Confirm Delete
                </p>
                <button
                  className="close-modal"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>

              <div style={styles.deleteConfirmContent}>
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  style={{ fontSize: "48px", color: "#EF4444" }}
                />
                <p>Are you sure you want to delete this receipt record?</p>
                <p style={styles.deleteConfirmSubtext}>
                  <strong>
                    {itemLookup[selectedMovement.inventory_item_id] ||
                      "Unknown"}
                  </strong>
                  <br />
                  Quantity: +{Math.abs(selectedMovement.quantity)} units
                  <br />
                  Total Value:{" "}
                  {formatCurrency(
                    Math.abs(selectedMovement.quantity) *
                      (selectedMovement.unit_cost || 0),
                  )}
                  <br />
                  <span style={{ color: "#EF4444" }}>
                    This will reverse the stock movement and remove the quantity
                    from inventory.
                  </span>
                </p>
              </div>

              <div style={styles.formActions}>
                <button
                  style={styles.cancelButton}
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
                <button
                  style={styles.deleteConfirmButton}
                  onClick={handleDeleteReceived}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <FontAwesomeIcon icon={faSpinner} spin />
                  ) : (
                    "Delete Receipt"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {modalSuccessMsg && (
        <MessageModal
          leftIcon={faCheckCircle}
          color="green"
          message={successMessage}
          onClose={() => setModalSuccessMsg(false)}
          rightIcon={faClose}
        />
      )}
      {modalError && (
        <MessageModal
          leftIcon={faTimes}
          color="red"
          message={errorMessage}
          onClose={() => setModalError(false)}
          rightIcon={faClose}
        />
      )}
    </div>
  );
}

const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#f5f7fa",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "24px 32px 4rem 32px",
  },
  loadingContainer: {
    textAlign: "center",
    padding: "80px 20px",
    backgroundColor: "white",
    borderRadius: "16px",
  },
  errorContainer: {
    textAlign: "center",
    padding: "80px 20px",
    backgroundColor: "white",
    borderRadius: "16px",
    color: "#d32f2f",
  },
  retryButton: {
    marginTop: "20px",
    padding: "10px 20px",
    backgroundColor: "#FF6B35",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  statisticsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "20px",
    marginBottom: "24px",
  },
  statCard: {
    background: "white",
    padding: "20px",
    borderRadius: "16px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    display: "flex",
    alignItems: "center",
    gap: "16px",
  },
  statIconContainer: {
    width: "56px",
    height: "56px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  statValue: {
    margin: 0,
    fontSize: "22px",
    fontWeight: "700",
    color: "#1F2937",
  },
  statLabel: {
    margin: "4px 0 0 0",
    color: "#6B7280",
    fontSize: "13px",
  },
  topItemsCard: {
    background: "white",
    padding: "20px",
    borderRadius: "16px",
    marginBottom: "24px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "#1F2937",
    marginBottom: "16px",
  },
  topItemsList: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },
  topItem: {
    display: "flex",
    alignItems: "center",
    gap: "16px",
    padding: "8px 12px",
    backgroundColor: "#F9FAFB",
    borderRadius: "8px",
  },
  topItemRank: {
    width: "28px",
    height: "28px",
    backgroundColor: "#FF6B35",
    color: "white",
    borderRadius: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: "bold",
    fontSize: "12px",
  },
  topItemName: {
    flex: 1,
    fontWeight: "500",
    color: "#1F2937",
  },
  topItemQty: {
    fontWeight: "600",
    color: "#FF6B35",
  },
  noDataText: {
    textAlign: "center",
    padding: "20px",
    color: "#9CA3AF",
  },
  filtersBar: {
    display: "flex",
    gap: "16px",
    marginBottom: "20px",
    flexWrap: "wrap",
    alignItems: "center",
  },
  searchWrapper: {
    flex: 2,
    position: "relative",
    minWidth: "250px",
  },
  searchIcon: {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#9CA3AF",
  },
  searchInput: {
    width: "100%",
    padding: "16px 16px 16px 40px",
    border: "1px solid #E5E7EB",
    borderRadius: "12px",
    fontSize: "15px",
    outline: "none",
    backgroundColor: "white",
  },
  clearSearchButton: {
    position: "absolute",
    right: "10px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    color: "#666",
    cursor: "pointer",
    fontSize: "14px",
  },
  dateWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 16px",
    backgroundColor: "white",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
  },
  dateSelect: {
    border: "none",
    fontSize: "14px",
    outline: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    padding: "4px 8px",
  },
  dateInput: {
    border: "none",
    fontSize: "14px",
    outline: "none",
    backgroundColor: "transparent",
  },
  customDateRange: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    padding: "10px 16px",
    backgroundColor: "white",
    borderRadius: "12px",
    border: "1px solid #E5E7EB",
  },
  refreshButton: {
    padding: "10px 16px",
    border: "1px solid #E5E7EB",
    borderRadius: "12px",
    backgroundColor: "white",
    fontSize: "14px",
    color: "#6B7280",
    cursor: "pointer",
  },
  tableCard: {
    background: "white",
    borderRadius: "16px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    overflow: "hidden",
  },
  tableContainer: {
    overflowX: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    minWidth: "1000px",
  },
  th: {
    textAlign: "left",
    padding: "16px",
    backgroundColor: "#000000",
    color: "#FFFFFF",
    fontWeight: "600",
    fontSize: "14px",
  },
  td: {
    padding: "14px 16px",
    borderBottom: "1px solid #F0F0F0",
    fontSize: "14px",
    color: "#1F2937",
  },
  tableRow: {
    cursor: "pointer",
    transition: "background-color 0.2s",
  },
  quantityBadge: {
    display: "inline-block",
    padding: "4px 10px",
    backgroundColor: "#D1FAE5",
    color: "#10B981",
    borderRadius: "20px",
    fontSize: "12px",
    fontWeight: "600",
  },
  actionButtons: {
    display: "flex",
    gap: "8px",
  },
  viewButton: {
    backgroundColor: "#FF6B35",
    color: "white",
    border: "none",
    borderRadius: "8px",
    padding: "6px 10px",
    cursor: "pointer",
  },
  editButton: {
    backgroundColor: "#E3F2FD",
    color: "#1976D2",
    border: "none",
    borderRadius: "6px",
    padding: "6px 10px",
    cursor: "pointer",
  },
  deleteButton: {
    backgroundColor: "#FEE2E2",
    color: "#D32F2F",
    border: "none",
    borderRadius: "6px",
    padding: "6px 10px",
    cursor: "pointer",
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "20px",
    marginTop: "24px",
    padding: "20px",
    backgroundColor: "white",
    borderRadius: "12px",
  },
  paginationButton: {
    padding: "8px 16px",
    backgroundColor: "#FF6B35",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  paginationInfo: {
    fontSize: "14px",
    color: "#6B7280",
  },
  emptyState: {
    textAlign: "center",
    padding: "80px 20px",
    backgroundColor: "white",
    borderRadius: "16px",
  },
  emptyTitle: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#1F2937",
    marginTop: "16px",
  },
  emptySubtitle: {
    fontSize: "14px",
    color: "#9CA3AF",
    marginTop: "8px",
  },
  // Receipt Modal Styles
  receiptContainer: {
    padding: "20px",
    backgroundColor: "#FFFFFF",
    fontFamily: "'Courier New', monospace",
  },
  receiptHeader: {
    textAlign: "center",
    marginBottom: "20px",
  },
  receiptTitle: {
    fontSize: "16px",
    fontWeight: "bold",
    color: "#FF6B35",
    margin: 0,
  },
  receiptSubtitle: {
    fontSize: "12px",
    color: "#6B7280",
    margin: "4px 0",
  },
  receiptDivider: {
    height: "1px",
    backgroundColor: "#000",
    margin: "10px 0",
  },
  receiptInfo: {
    marginBottom: "16px",
  },
  receiptRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "6px",
    fontSize: "12px",
  },
  receiptLabel: {
    color: "#64748B",
  },
  receiptValue: {
    color: "#1E293B",
    fontWeight: "500",
  },
  receiptFooter: {
    textAlign: "center",
    marginTop: "16px",
  },
  modalActions: {
    display: "flex",
    gap: "12px",
    marginTop: "20px",
    paddingTop: "16px",
    borderTop: "1px solid #E2E8F0",
  },
  printButton: {
    flex: 1,
    padding: "10px",
    backgroundColor: "#FF6B35",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  closeButton: {
    flex: 1,
    padding: "10px",
    backgroundColor: "#64748B",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  disabledInput: {
    backgroundColor: "#F9FAFB",
    border: "1px solid #E5E7EB",
    borderRadius: "6px",
    padding: "8px 12px",
    color: "#6B7280",
    cursor: "not-allowed",
    width: "100%",
  },
  formActions: {
    display: "flex",
    gap: "12px",
    marginTop: "20px",
  },
  cancelButton: {
    flex: 1,
    padding: "10px",
    backgroundColor: "#F3F4F6",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  submitButton: {
    flex: 1,
    padding: "10px",
    backgroundColor: "#FF6B35",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  deleteConfirmButton: {
    flex: 1,
    padding: "10px",
    backgroundColor: "#EF4444",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    fontWeight: "500",
  },
  deleteConfirmContent: {
    textAlign: "center",
    padding: "20px",
  },
  deleteConfirmSubtext: {
    fontSize: "13px",
    color: "#6B7280",
    marginTop: "12px",
    lineHeight: "1.5",
  },
};
